import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { ArrowLeft, Save, ShieldAlert, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Loader from "../../../../components/UI/Loader/Loader";
import { getCSPConfig, updateCSPConfig } from "../../../../utils/APIs";
import { useUserContext } from "../../../../contexts/UserContext";
import Card from "../../../../components/UI/Card";
import Button from "../../../../components/UI/Button";
import { Textarea } from "../../../../components/UI";

const CSP_FIELDS = [
    {
        key: "imgDomains",
        label: "Image Sources (img-src)",
        description: "Allowed domains to load images from (e.g. https://cms.vodafone.com.tr)"
    },
    {
        key: "scriptDomains",
        label: "Script Sources (script-src)",
        description: "Allowed domains to load JavaScript from (e.g. https://js.stripe.com)"
    },
    {
        key: "connectDomains",
        label: "Connect Sources (connect-src)",
        description: "Allowed domains for XHR, WebSockets, and fetch requests"
    },
    {
        key: "frameDomains",
        label: "Frame Sources (frame-src)",
        description: "Allowed domains that can be embedded as iframes"
    }
];

const ManageCSP = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        imgDomains: "",
        scriptDomains: "",
        connectDomains: "",
        frameDomains: ""
    });

    const { userData } = useUserContext();

    useEffect(() => {
        const fetchCSPConfig = async () => {
            try {
                const response = await getCSPConfig(userData?.token, userData?.selectedOrganization?.org_token);
                if (response?.status) {
                    setFormData(response.data);
                }
            } catch (error) {
                console.error("Failed to fetch CSP config:", error);
                toast.error("Failed to load CSP configuration");
            } finally {
                setIsLoading(false);
            }
        };

        fetchCSPConfig();
    }, [userData]);

    const handleChange = (key, value) => {
        setFormData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const response = await updateCSPConfig(userData?.token, userData?.selectedOrganization?.org_token, formData);
            if (response?.status) {
                if (response.message?.includes("warning")) {
                    toast.warning(response.message, { autoClose: 10000 });
                } else {
                    toast.success("CSP configuration updated successfully");
                }
            }
        } catch (error) {
            console.error("Failed to save CSP config:", error);
            toast.error(error?.message || "Failed to save configuration");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) return <Loader />;

    return (
        <div className="stride-page-shell">
            <div className="max-w-4xl mx-auto space-y-6">
                <div className="flex items-center gap-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        icon={<ArrowLeft size={16} />}
                        onClick={() => navigate("/admin")}
                        aria-label="Back to Admin"
                    />
                    <div>
                        <span className="stride-eyebrow">Administration</span>
                        <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                            Manage Content Security Policy
                        </h1>
                        <p className="text-sm text-stride-body mt-0.5">
                            Configure allowed origin domains for Content Security Policy (CSP) directives.
                        </p>
                    </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/25 rounded-xl">
                    <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm">
                        <h3 className="font-semibold text-amber-900 mb-0.5">Security Notice</h3>
                        <p className="text-amber-800/90 leading-relaxed text-xs">
                            Modifying these directives controls resource loading across the entire application. Only whitelist trusted domains.
                            Separate multiple origins with spaces (e.g. <code className="bg-amber-100/80 px-1 py-0.5 rounded font-mono text-[11px] text-amber-900">https://example.com https://api.example.com</code>).
                        </p>
                    </div>
                </div>

                <Card padding="none" className="overflow-hidden">
                    <form onSubmit={handleSubmit}>
                        <div className="p-6 space-y-6">
                            {CSP_FIELDS.map((field) => (
                                <div key={field.key} className="space-y-1.5 relative group">
                                    <div className="flex items-center justify-between">
                                        <label className="block text-sm font-semibold text-stride-heading">
                                            {field.label}
                                        </label>
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                            <CheckCircle2 className={`w-4 h-4 ${formData[field.key] ? 'text-emerald-500' : 'text-stride-muted'}`} />
                                        </div>
                                    </div>
                                    <Textarea
                                        value={formData[field.key] || ""}
                                        onChange={(e) => handleChange(field.key, e.target.value)}
                                        placeholder="e.g. https://domain.com https://another.com"
                                        helperText={field.description}
                                        inputClassName="font-mono text-xs"
                                        rows={3}
                                    />
                                </div>
                            ))}
                        </div>

                        <div className="bg-stride-bg px-6 py-4 border-t border-stride-border flex justify-end gap-2.5">
                            <Button
                                variant="secondary"
                                type="button"
                                onClick={() => navigate("/admin")}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                loading={isSaving}
                                disabled={isSaving}
                                icon={<Save size={15} />}
                            >
                                Save Configuration
                            </Button>
                        </div>
                    </form>
                </Card>
            </div>
        </div>
    );
};

export default ManageCSP;
